import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Boxes,
  ChevronDown,
  Link as LinkIcon,
  Minus,
  PackageSearch,
  Plus,
  Search,
  X,
} from "lucide-react";
import {
  getApprovedQtyPQC,
  listAllPreQuoteBrands,
  listAllPreQuoteSets,
  listAllPreQuotedCooks,
} from "../../reducers/prequotedcooks.slice";
import { createOrder } from "../../reducers/order.slice";
import { formatNumberToDollar } from "../../utils/util";
import { AuthContext } from "../../context/authContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import PageHeader from "../../components/shared/PageHeader";
import ViewToggle from "../../components/shared/ViewToggle";
import FieldBlock from "../../components/shared/FieldBlock";
import useMediaQuery from "../../components/shared/useMediaQuery";
import CreateOrderAgreement from "../../components/shared/CreateOrderAgreement";

const FALLBACK_IMAGE = "https://cdn.pixabay.com/photo/2024/06/26/23/36/package-8856091_1280.png";
const ROWS_PER_PAGE = 24;
const CONTENT_MAX_WIDTH = 1280;
const LIST_FACT_PROPS = { labelSize: 9.5, valueSize: 12, valueWeight: 700 };

const SORT_OPTIONS = [
  { label: "Newest", sort_by: "created_at", sort_order: "DESC" },
  { label: "Most sold", sort_by: "approved_quantity", sort_order: "DESC" },
  { label: "Highest payout", sort_by: "cashout_price", sort_order: "DESC" },
  { label: "Lowest payout", sort_by: "cashout_price", sort_order: "ASC" },
  { label: "A-Z", sort_by: "item_name", sort_order: "ASC" },
];

const getRemainingQuantity = (item) =>
  Math.max(0, Number(item?.total_quantity || 0) - Number(item?.available_quantity || 0));

const orderBrands = (rows = []) =>
  [...rows].sort((a, b) => {
    const aIsOthers = a?.name?.trim().toLowerCase() === "others";
    const bIsOthers = b?.name?.trim().toLowerCase() === "others";
    if (aIsOthers === bIsOthers) return 0;
    return aIsOthers ? 1 : -1;
  });

const handleImageFallback = (event) => {
  if (event.currentTarget.src !== FALLBACK_IMAGE) {
    event.currentTarget.src = FALLBACK_IMAGE;
  }
};

const SellPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 760px)");
  const isPhone = useMediaQuery("(max-width: 640px)");
  const isCompactList = useMediaQuery("(max-width: 1100px)");

  const [catalogBrands, setCatalogBrands] = useState([]);
  const [catalogSets, setCatalogSets] = useState([]);
  const [searchCounts, setSearchCounts] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const [selectedSetId, setSelectedSetId] = useState("");
  const [brandSearch, setBrandSearch] = useState("");
  const [setSearch, setSetSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortOption, setSortOption] = useState(SORT_OPTIONS[0]);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("sellViewMode") || "card");
  const [page, setPage] = useState(0);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingSets, setLoadingSets] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImageProduct, setSelectedImageProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderLines, setOrderLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [agreementError, setAgreementError] = useState(false);
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const [setPickerOpen, setSetPickerOpen] = useState(false);

  const brands = useMemo(
    () => debouncedSearch
      ? catalogBrands.map((brand) => ({
          ...brand,
          product_count: searchCounts?.search === debouncedSearch ? searchCounts.brands[brand.id] || 0 : 0,
        }))
      : catalogBrands,
    [catalogBrands, debouncedSearch, searchCounts]
  );

  const sets = useMemo(
    () => debouncedSearch
      ? catalogSets.map((set) => ({
          ...set,
          product_count: searchCounts?.search === debouncedSearch ? searchCounts.sets[set.id] || 0 : 0,
        }))
      : catalogSets,
    [catalogSets, debouncedSearch, searchCounts]
  );

  const selectedBrand = useMemo(
    () => brands.find((brand) => brand.id === selectedBrandId),
    [brands, selectedBrandId]
  );

  const selectedSet = useMemo(
    () => sets.find((set) => set.id === selectedSetId),
    [sets, selectedSetId]
  );

  const filteredBrands = useMemo(() => {
    const query = brandSearch.trim().toLowerCase();
    const rows = query ? brands.filter((brand) => brand.name?.toLowerCase().includes(query)) : brands;
    return orderBrands(rows);
  }, [brands, brandSearch]);

  const filteredSets = useMemo(() => {
    const query = setSearch.trim().toLowerCase();
    return query ? sets.filter((set) => set.name?.toLowerCase().includes(query)) : sets;
  }, [sets, setSearch]);

  const productTotal = selectedSet?.product_count ?? selectedBrand?.product_count ?? products.length;
  const canGoNext = !loadingProducts && products.length === ROWS_PER_PAGE &&
    (!debouncedSearch || (page + 1) * ROWS_PER_PAGE < productTotal);
  const canGoPrev = page > 0;
  const hasSetRail = selectedBrandId && (loadingSets || sets.length > 0);

  useEffect(() => {
    setLoadingBrands(true);
    dispatch(listAllPreQuoteBrands()).then((res) => {
      setLoadingBrands(false);
      if (res?.error && Object.keys(res.error).length > 0) {
        showToast(res.error.message, "error");
        return;
      }
      const rows = orderBrands(res?.payload || []);
      setCatalogBrands(rows);
      setSelectedBrandId((current) => current || rows?.[0]?.id || "");
    });
  }, [dispatch, showToast]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(productSearch.trim()), 450);
    return () => clearTimeout(timer);
  }, [productSearch]);

  useEffect(() => {
    if (!debouncedSearch) {
      setSearchCounts(null);
      return;
    }

    let active = true;
    dispatch(listAllPreQuotedCooks({ search: debouncedSearch })).then((res) => {
      if (!active) return;
      if (res?.error && Object.keys(res.error).length > 0) {
        showToast(res.error.message, "error");
        return;
      }
      const brandCounts = {};
      const setCounts = {};
      for (const product of res?.payload || []) {
        if (product.brand_id) {
          brandCounts[product.brand_id] = (brandCounts[product.brand_id] || 0) + 1;
        }
        if (product.set_id) {
          setCounts[product.set_id] = (setCounts[product.set_id] || 0) + 1;
        }
      }
      setSearchCounts({ search: debouncedSearch, brands: brandCounts, sets: setCounts });
    });

    return () => {
      active = false;
    };
  }, [debouncedSearch, dispatch, showToast]);

  useEffect(() => {
    if (!selectedBrandId) {
      setCatalogSets([]);
      setProducts([]);
      setLoadingSets(false);
      return;
    }

    let active = true;
    setSelectedSetId("");
    setPage(0);
    setCatalogSets([]);
    setLoadingSets(true);
    dispatch(listAllPreQuoteSets({ brand_id: selectedBrandId })).then((res) => {
      if (!active) return;
      setLoadingSets(false);
      if (res?.error && Object.keys(res.error).length > 0) {
        showToast(res.error.message, "error");
        return;
      }
      setCatalogSets(res?.payload || []);
    });

    return () => {
      active = false;
    };
  }, [dispatch, selectedBrandId, showToast]);

  useEffect(() => {
    if (!selectedBrandId) return;

    let active = true;
    setLoadingProducts(true);
    dispatch(
      listAllPreQuotedCooks({
        brand_id: selectedBrandId,
        set_id: selectedSetId || undefined,
        skip: page * ROWS_PER_PAGE,
        limit: ROWS_PER_PAGE,
        search: debouncedSearch || undefined,
        sort_by: sortOption.sort_by,
        sort_order: sortOption.sort_order,
      })
    ).then((res) => {
      if (!active) return;
      setLoadingProducts(false);
      if (res?.error && Object.keys(res.error).length > 0) {
        showToast(res.error.message, "error");
        return;
      }
      setProducts(res?.payload || []);
    });

    return () => {
      active = false;
    };
  }, [debouncedSearch, dispatch, page, selectedBrandId, selectedSetId, showToast, sortOption]);

  const handleViewChange = (newView) => {
    setViewMode(newView);
    localStorage.setItem("sellViewMode", newView);
  };

  const handleBrandSelect = (brandId) => {
    setSelectedSetId("");
    setSetSearch("");
    setPage(0);
    setSelectedBrandId(brandId);
    setBrandPickerOpen(false);
  };

  const handleSetSelect = (setId) => {
    setSelectedSetId(setId);
    setPage(0);
    setSetPickerOpen(false);
  };

  const handleProductToggle = (item) => {
    if (getRemainingQuantity(item) === 0) return;
    setSelectedProducts((prev) => {
      const selected = prev.some((product) => product.id === item.id);
      return selected ? prev.filter((product) => product.id !== item.id) : [...prev, item];
    });
  };

  const openImageDialog = (item) => {
    setSelectedImageProduct(item);
    setImageDialogOpen(true);
  };

  const handleOpenOrderDialog = () => {
    if (!selectedProducts.length) return;
    const lines = selectedProducts.map((item) => ({
      id: item.id,
      item_name: item.item_name,
      item_link: item.item_link,
      upc: item.upc,
      retailer_name: item.retailer_name,
      buy_price: item.buy_price,
      cashout_price: item.cashout_price,
      quantity: 1,
      maxQty: null,
      loadingMax: true,
    }));
    setOrderLines(lines);
    setAgreementChecked(false);
    setAgreementError(false);
    setOrderDialogOpen(true);

    selectedProducts.forEach((item) => {
      dispatch(getApprovedQtyPQC({ id: item.id })).then((res) => {
        const allowed = Number(res?.payload?.allowed_quantity) || 0;
        setOrderLines((prev) =>
          prev.map((line) =>
            line.id === item.id
              ? {
                  ...line,
                  maxQty: allowed,
                  loadingMax: false,
                  quantity: allowed > 0 ? Math.min(line.quantity, allowed) : 0,
                }
              : line
          )
        );
      });
    });
  };

  const handleQtyStep = (id, delta) => {
    setOrderLines((prev) =>
      prev.map((line) => {
        if (line.id !== id || line.loadingMax) return line;
        const max = line.maxQty || 0;
        return { ...line, quantity: Math.max(0, Math.min(max, line.quantity + delta)) };
      })
    );
  };

  const handleQtyInputChange = (id, rawValue) => {
    const digits = rawValue.replace(/\D/g, "");
    setOrderLines((prev) =>
      prev.map((line) => {
        if (line.id !== id || line.loadingMax) return line;
        const max = line.maxQty || 0;
        const parsed = digits === "" ? 0 : parseInt(digits, 10);
        return { ...line, quantity: Math.max(0, Math.min(max, parsed)) };
      })
    );
  };

  const handleRemoveLine = (id) => {
    setOrderLines((prev) => prev.filter((line) => line.id !== id));
    setSelectedProducts((prev) => prev.filter((product) => product.id !== id));
  };

  const handleOrderDialogChange = (open) => {
    setOrderDialogOpen(open);
    if (!open) {
      setOrderLines([]);
      setAgreementChecked(false);
    }
  };

  const handleConfirmCreateOrder = () => {
    if (!orderLines.length) return;
    if (!agreementChecked) {
      setAgreementError(true);
      showToast("Please accept the order agreement before creating the order.", "error");
      return;
    }
    const hasZeroQuantity = orderLines.some((line) => Number(line.quantity) === 0);
    if (hasZeroQuantity) {
      showToast("Quantity cannot be zero. Please update or remove all items.", "error");
      return;
    }

    setSubmitting(true);
    const order_items = orderLines.map((line) => ({
      item_name: line.item_name,
      item_link: line.item_link || "",
      upc: line.upc || "",
      quote_id: line.id || "",
      quantity: Number(line.quantity),
      buy_price: line.buy_price || 0,
      cashout_price: line.cashout_price || 0,
      total_amount: (line.cashout_price || 0) * Number(line.quantity),
      retailer_name: line.retailer_name || "",
    }));

    dispatch(createOrder({ type: "PRE_QUOTE", order_items })).then((res) => {
      if (res?.error && Object.keys(res.error).length > 0) {
        setSubmitting(false);
        showToast(res.error.message, "error");
        return;
      }
      setSubmitting(false);
      setOrderDialogOpen(false);
      setOrderLines([]);
      setAgreementChecked(false);
      setSelectedProducts([]);
      showToast(
        `Order created with ${order_items.length} item${order_items.length > 1 ? "s" : ""} - pending approval in Open Orders`,
        "success"
      );
      navigate("/dashboard/open-orders");
    });
  };

  const renderBrandBar = () => (
    <section
      style={{
        marginBottom: 12,
        paddingBottom: 12,
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: isPhone ? "stretch" : "center",
          justifyContent: "space-between",
          gap: isPhone ? 8 : 12,
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <div style={{ color: "#9fb3c8", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em" }}>
          BRANDS
        </div>
        {!isPhone && (
          <div style={{ position: "relative", flex: "0 1 300px", minWidth: 0 }}>
            <Search size={14} style={{ position: "absolute", top: 10, left: 11, color: "#8b9296" }} />
            <Input
              value={brandSearch}
              onChange={(e) => setBrandSearch(e.target.value)}
              placeholder="Find brand..."
              style={{ paddingLeft: 34, height: 36 }}
            />
          </div>
        )}
      </div>
      {isPhone ? (
        <MobileSelectorButton
          disabled={loadingBrands || brands.length === 0}
          label={loadingBrands ? "Loading brands..." : selectedBrand?.name || "Choose brand"}
          meta={!loadingBrands && selectedBrand ? `${selectedBrand.product_count || 0} products` : undefined}
          onClick={() => setBrandPickerOpen(true)}
        />
      ) : (
        <div
          style={{
            display: "flex",
            gap: 8,
            overflowX: "auto",
            paddingBottom: 3,
            minWidth: 0,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {loadingBrands ? (
            <Chip active={false}>Loading brands...</Chip>
          ) : filteredBrands.length > 0 ? (
            filteredBrands.map((brand) => {
              const active = brand.id === selectedBrandId;
              return (
                <BrandChip
                  key={brand.id}
                  active={active}
                  name={brand.name}
                  count={brand.product_count || 0}
                  onClick={() => handleBrandSelect(brand.id)}
                />
              );
            })
          ) : (
            <Chip active={false}>No brands found</Chip>
          )}
        </div>
      )}
    </section>
  );

  const renderBrandPickerDialog = () => (
    <Dialog open={brandPickerOpen} onOpenChange={setBrandPickerOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Choose brand</DialogTitle>
          <DialogDescription>Search and select a Sell brand.</DialogDescription>
        </DialogHeader>
        <div style={{ position: "relative", marginBottom: 12 }}>
          <Search size={14} style={{ position: "absolute", top: 10, left: 11, color: "#8b9296" }} />
          <Input
            value={brandSearch}
            onChange={(e) => setBrandSearch(e.target.value)}
            placeholder="Find brand..."
            style={{ paddingLeft: 34, height: 36 }}
          />
        </div>
        <div className="member-scroll" style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "52vh", overflowY: "auto", paddingRight: 2 }}>
          {loadingBrands ? (
            <PickerRow label="Loading brands..." />
          ) : filteredBrands.length > 0 ? (
            filteredBrands.map((brand) => (
              <PickerRow
                key={brand.id}
                active={brand.id === selectedBrandId}
                label={brand.name}
                meta={`${brand.product_count || 0} products`}
                onClick={() => handleBrandSelect(brand.id)}
              />
            ))
          ) : (
            <PickerRow label="No brands found" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  const renderSetPickerDialog = () => (
    <Dialog open={setPickerOpen} onOpenChange={setSetPickerOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Choose set</DialogTitle>
          <DialogDescription>Filter this brand by set, or keep all sets selected.</DialogDescription>
        </DialogHeader>
        <div style={{ position: "relative", marginBottom: 12 }}>
          <Search size={14} style={{ position: "absolute", top: 10, left: 11, color: "#8b9296" }} />
          <Input
            value={setSearch}
            onChange={(e) => setSetSearch(e.target.value)}
            placeholder="Find set..."
            style={{ paddingLeft: 34, height: 36 }}
          />
        </div>
        <div className="member-scroll" style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "52vh", overflowY: "auto", paddingRight: 2 }}>
          <PickerRow
            active={!selectedSetId}
            label="All sets"
            meta={`${selectedBrand?.product_count || 0} products`}
            onClick={() => handleSetSelect("")}
          />
          {filteredSets.length > 0 ? (
            filteredSets.map((set) => (
              <PickerRow
                key={set.id}
                active={set.id === selectedSetId}
                label={set.name}
                meta={`${set.product_count || 0} products`}
                onClick={() => handleSetSelect(set.id)}
              />
            ))
          ) : (
            <PickerRow label="No sets found" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  const renderSetChips = () => {
    if (loadingSets) {
      if (isPhone) {
        return (
          <div style={{ margin: "8px 0 16px", minHeight: 70 }}>
            <div style={{ color: "#9fb3c8", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", marginBottom: 8 }}>
              SETS
            </div>
            <MobileSelectorButton disabled label="Loading sets..." />
          </div>
        );
      }

      return (
        <div style={{ display: "flex", gap: 8, paddingBottom: 3, margin: "8px 0 16px", minHeight: 41 }}>
          <Chip active={false}>Loading sets...</Chip>
        </div>
      );
    }

    if (!sets.length) return null;
    if (isPhone) {
      return (
        <div style={{ margin: "8px 0 16px" }}>
          <div style={{ color: "#9fb3c8", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", marginBottom: 8 }}>
            SETS
          </div>
          <MobileSelectorButton
            label={selectedSet?.name || "All sets"}
            meta={`${selectedSet?.product_count ?? selectedBrand?.product_count ?? 0} products`}
            onClick={() => setSetPickerOpen(true)}
          />
        </div>
      );
    }

    return (
      <div
        style={{
          display: "flex",
          gap: 8,
          overflowX: "auto",
          paddingBottom: 3,
          margin: "8px 0 16px",
          minWidth: 0,
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Chip active={!selectedSetId} onClick={() => handleSetSelect("")}>
          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>All sets</span>
          <CountBadge active={!selectedSetId}>{selectedBrand?.product_count || 0}</CountBadge>
        </Chip>
        {sets.map((set) => (
          <Chip key={set.id} active={selectedSetId === set.id} onClick={() => handleSetSelect(set.id)}>
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{set.name}</span>
            <CountBadge active={selectedSetId === set.id}>{set.product_count || 0}</CountBadge>
          </Chip>
        ))}
      </div>
    );
  };

  const renderProductCard = (item) => {
    const selected = selectedProducts.some((product) => product.id === item.id);
    const disabled = getRemainingQuantity(item) === 0;
    return (
      <div
        key={item.id}
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          borderRadius: 14,
          border: selected ? "1px solid #00ADB5" : "1px solid #2a2a2e",
          boxShadow: selected ? "0 0 0 1px #00ADB5 inset" : "none",
          background: "#1e1e1e",
          opacity: disabled ? 0.55 : 1,
          overflow: "hidden",
          transition: "border-color 0.15s",
        }}
      >
        <button
          type="button"
          aria-label="select item"
          disabled={disabled}
          onClick={() => handleProductToggle(item)}
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            borderRadius: 6,
            border: selected ? "2px solid #00ADB5" : "2px solid #2a2a2e",
            background: selected ? "#00ADB5" : "rgba(0,0,0,0.4)",
            cursor: disabled ? "not-allowed" : "pointer",
            color: "#00110f",
            fontSize: 13,
            fontWeight: 800,
          }}
        >
          {selected ? "✓" : ""}
        </button>
        <div
          onClick={() => openImageDialog(item)}
          style={{
            height: 130,
            background: "#fff",
            borderBottom: "1px solid #2a2a2e",
            cursor: "pointer",
          }}
        >
          <img
            src={item?.item_image_url || FALLBACK_IMAGE}
            alt={item?.item_name}
            onError={handleImageFallback}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>
        <div style={{ padding: 14 }}>
          <div
            title={item?.item_name}
            style={{
              color: "#fff",
              fontSize: 13.5,
              fontWeight: 700,
              lineHeight: 1.3,
              minHeight: 35,
              paddingRight: 20,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflowWrap: "anywhere",
            }}
          >
            {item?.item_name}
          </div>
          <div
            style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 10,
              color: "#c3c8cc",
              margin: "3px 0 10px",
              overflowWrap: "anywhere",
            }}
          >
            ID: {item?.id}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "8px 12px",
              fontSize: 11,
              marginBottom: 10,
            }}
          >
            <FieldBlock label="UPC" value={item?.upc || "N/A"} wrap />
            <FieldBlock label="RETAILER" value={item?.retailer_name || "N/A"} wrap={isMobile} align="right" />
            <FieldBlock label="BUY PRICE" value={formatNumberToDollar(item?.buy_price || 0)} wrap={isMobile} />
            <FieldBlock label="CASHOUT" value={formatNumberToDollar(item?.cashout_price || 0)} color="#4caf50" wrap={isMobile} align="right" />
            <FieldBlock
              label="QUANTITY"
              value={`0/${getRemainingQuantity(item)}`}
              wrap={isMobile}
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
              paddingTop: 10,
              borderTop: "1px solid #2a2a2e",
            }}
          >
            <button
              type="button"
              onClick={() => {
                if (item?.item_link) window.open(item.item_link, "_blank");
                else showToast("Item link not available", "error");
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                background: "transparent",
                color: "#22d3ee",
                padding: 0,
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                whiteSpace: "nowrap",
                border: "none",
              }}
            >
              <LinkIcon size={12} style={{ marginRight: 5 }} />
              View Product
            </button>
            <Button size="xs" disabled={disabled} onClick={() => handleProductToggle(item)}>
              {selected ? "✓ Added" : "+ Add"}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderProductList = () => {
    if (isPhone) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {products.map((item) => {
            const selected = selectedProducts.some((product) => product.id === item.id);
            const disabled = getRemainingQuantity(item) === 0;
            return (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  border: selected ? "1px solid rgba(0,173,181,0.36)" : "1px solid #24303a",
                  boxShadow: selected ? "0 0 0 1px rgba(0,173,181,0.18) inset" : "none",
                  borderRadius: 14,
                  background: "#1e1e1e",
                  padding: 10,
                  opacity: disabled ? 0.55 : 1,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "64px minmax(0, 1fr)",
                    gap: 10,
                    alignItems: "start",
                    minWidth: 0,
                  }}
                >
                  <img
                    src={item?.item_image_url || FALLBACK_IMAGE}
                    alt={item?.item_name}
                    onError={handleImageFallback}
                    onClick={() => openImageDialog(item)}
                    style={{
                      width: 64,
                      height: 64,
                      objectFit: "contain",
                      borderRadius: 10,
                      border: "1px solid #25323c",
                      cursor: "pointer",
                      background: "#fff",
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div
                      title={item?.item_name}
                      style={{
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: 13.5,
                        lineHeight: 1.3,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {item?.item_name}
                    </div>
                    <div
                      style={{
                        color: "#c3c8cc",
                        fontSize: 10,
                        fontFamily: "ui-monospace, monospace",
                        marginTop: 5,
                        overflowWrap: "anywhere",
                      }}
                    >
                      ID: {item?.id}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: "8px 12px",
                    minWidth: 0,
                  }}
                >
                  <FieldBlock label="UPC" value={item?.upc || "N/A"} wrap />
                  <FieldBlock label="RETAILER" value={item?.retailer_name || "N/A"} wrap align="right" />
                  <FieldBlock label="BUY PRICE" value={formatNumberToDollar(item?.buy_price || 0)} wrap />
                  <FieldBlock label="CASHOUT" value={formatNumberToDollar(item?.cashout_price || 0)} color="#4caf50" wrap align="right" />
                  <FieldBlock label="QUANTITY" value={`0/${getRemainingQuantity(item)}`} wrap />
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                    paddingTop: 10,
                    borderTop: "1px solid #2a2a2e",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (item?.item_link) window.open(item.item_link, "_blank");
                      else showToast("Item link not available", "error");
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      background: "transparent",
                      border: "none",
                      color: "#22d3ee",
                      padding: 0,
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <LinkIcon size={12} style={{ marginRight: 5 }} />
                    View Product
                  </button>
                  <Button size="xs" disabled={disabled} onClick={() => handleProductToggle(item)}>
                    {selected ? "✓ Added" : "+ Add"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {products.map((item) => {
          const selected = selectedProducts.some((product) => product.id === item.id);
          const disabled = getRemainingQuantity(item) === 0;
          const detailGridColumns = isCompactList
            ? "repeat(auto-fit, minmax(96px, 1fr))"
            : "minmax(118px, 1.2fr) minmax(82px, 0.85fr) minmax(72px, 0.7fr) minmax(78px, 0.75fr) minmax(64px, 0.65fr)";
          return (
            <div
              key={item.id}
              style={{
                display: "grid",
                gridTemplateColumns: isCompactList
                  ? "72px minmax(0, 1fr) 116px"
                  : "72px minmax(250px, 0.85fr) minmax(0, 2.2fr) 124px",
                columnGap: isCompactList ? 12 : 18,
                rowGap: 12,
                alignItems: "center",
                border: selected ? "1px solid rgba(0,173,181,0.32)" : "1px solid #24303a",
                borderRadius: 14,
                background: "#1e1e1e",
                padding: isCompactList ? 12 : "14px 14px 14px 16px",
                opacity: disabled ? 0.55 : 1,
                minWidth: 0,
              }}
            >
              <img
                src={item?.item_image_url || FALLBACK_IMAGE}
                alt={item?.item_name}
                onError={handleImageFallback}
                onClick={() => openImageDialog(item)}
                style={{
                  width: 64,
                  height: 64,
                  objectFit: "contain",
                  borderRadius: 10,
                  border: "1px solid #25323c",
                  cursor: "pointer",
                  background: "#fff",
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "#fff", fontWeight: 800, fontSize: isCompactList ? 13.5 : 13, lineHeight: 1.3, overflowWrap: "anywhere" }}>
                  {item?.item_name}
                </div>
                <div
                  style={{
                    color: "#c3c8cc",
                    fontSize: 10,
                    fontFamily: "ui-monospace, monospace",
                    marginTop: 5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  ID: {item?.id}
                </div>
              </div>
              <div
                style={{
                  gridColumn: isCompactList ? "1 / -1" : undefined,
                  display: "grid",
                  gridTemplateColumns: detailGridColumns,
                  gap: isCompactList ? "8px 12px" : "8px 14px",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <FieldBlock label="UPC" value={item?.upc || "N/A"} wrap {...LIST_FACT_PROPS} />
                <FieldBlock label="RETAILER" value={item?.retailer_name || "N/A"} {...LIST_FACT_PROPS} />
                <FieldBlock label="BUY PRICE" value={formatNumberToDollar(item?.buy_price || 0)} {...LIST_FACT_PROPS} />
                <FieldBlock label="CASHOUT" value={formatNumberToDollar(item?.cashout_price || 0)} color="#fff" {...LIST_FACT_PROPS} />
                <FieldBlock label="QUANTITY" value={`0/${getRemainingQuantity(item)}`} {...LIST_FACT_PROPS} />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  justifyContent: "center",
                  gap: 10,
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <Button
                  variant={selected ? "outline" : "default"}
                  size="sm"
                  disabled={disabled}
                  onClick={() => handleProductToggle(item)}
                  style={{
                    width: "100%",
                    height: 42,
                    minWidth: 0,
                    borderRadius: 10,
                  }}
                >
                  {selected ? "Remove" : "Add"}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    if (item?.item_link) window.open(item.item_link, "_blank");
                    else showToast("Item link not available", "error");
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    minWidth: 0,
                    minHeight: 24,
                    border: "none",
                    background: "transparent",
                    color: "#9fb3c8",
                    padding: 0,
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <LinkIcon size={13} style={{ marginRight: 5, color: "#8b9296" }} />
                  View Product
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const orderTotal = orderLines.reduce((sum, line) => sum + (line.cashout_price || 0) * (line.quantity || 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
      {renderBrandPickerDialog()}
      {renderSetPickerDialog()}

      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedImageProduct?.item_name || "Product image"}</DialogTitle>
            {selectedImageProduct?.description && (
              <DialogDescription>{selectedImageProduct.description}</DialogDescription>
            )}
          </DialogHeader>
          <div style={{ height: isMobile ? 240 : 340, borderRadius: 10, border: "1px solid #24303a", background: "#fff" }}>
            <img
              src={selectedImageProduct?.item_image_url || FALLBACK_IMAGE}
              alt={selectedImageProduct?.item_name}
              onError={handleImageFallback}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={orderDialogOpen} onOpenChange={handleOrderDialogChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create order - set quantities</DialogTitle>
            <DialogDescription>Confirm how many units of each item you're sending.</DialogDescription>
          </DialogHeader>
          <div style={{ maxHeight: isPhone ? "48vh" : 340, overflowY: "auto", overflowX: "hidden" }}>
            {orderLines.map((line) => (
              <div
                key={line.id}
                style={{
                  display: "flex",
                  alignItems: isMobile ? "stretch" : "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexDirection: isMobile ? "column" : "row",
                  padding: "11px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: "#fff", fontSize: 13, fontWeight: 700, overflowWrap: "anywhere" }}>{line.item_name}</div>
                  <div style={{ color: "#9fb3c8", fontSize: 11, marginTop: 2 }}>
                    @ {formatNumberToDollar(line.cashout_price || 0)} cashout -{" "}
                    {line.loadingMax ? "checking allowed qty..." : `${line.maxQty || 0} available`}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", flexShrink: 0, justifyContent: isPhone ? "flex-start" : undefined, minWidth: 0 }}>
                  <StepperButton disabled={line.loadingMax || line.quantity <= 0} onClick={() => handleQtyStep(line.id, -1)}>
                    <Minus size={13} />
                  </StepperButton>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={line.quantity}
                    disabled={line.loadingMax}
                    onChange={(e) => handleQtyInputChange(line.id, e.target.value)}
                    style={{
                      width: 44,
                      height: 30,
                      textAlign: "center",
                      background: "#000",
                      border: "1px solid #2a2a2e",
                      color: "#fff",
                      fontFamily: "ui-monospace, monospace",
                    }}
                  />
                  <StepperButton disabled={line.loadingMax || line.quantity >= (line.maxQty || 0)} onClick={() => handleQtyStep(line.id, 1)}>
                    <Plus size={13} />
                  </StepperButton>
                  <button
                    type="button"
                    aria-label="remove item"
                    onClick={() => handleRemoveLine(line.id)}
                    style={{ width: 30, height: 30, marginLeft: 8, border: "none", background: "transparent", color: "#9fb3c8", cursor: "pointer" }}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              background: "#1e1e1e",
              borderRadius: 10,
              padding: "12px 14px",
              marginTop: 14,
            }}
          >
            <span style={{ color: "#9fb3c8" }}>Estimated cashout</span>
            <b style={{ color: "#fff", fontFamily: "ui-monospace, monospace" }}>{formatNumberToDollar(orderTotal)}</b>
          </div>
          <CreateOrderAgreement
            checked={agreementChecked}
            onChange={(value) => {
              setAgreementChecked(value);
              setAgreementError(false);
            }}
            error={agreementError}
          />
          <DialogFooter style={{ justifyContent: isPhone ? "stretch" : "flex-end" }}>
            <Button variant="outline" onClick={() => handleOrderDialogChange(false)} style={{ width: isPhone ? "100%" : undefined }}>
              Back
            </Button>
            <Button
              onClick={handleConfirmCreateOrder}
              disabled={submitting || orderLines.length === 0 || orderLines.some((line) => line.loadingMax)}
              style={{ width: isPhone ? "100%" : undefined }}
            >
              {submitting ? "Creating..." : "Create order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div
        style={{
          width: "100%",
          maxWidth: CONTENT_MAX_WIDTH,
          margin: "0 auto",
          padding: isPhone ? 0 : isMobile ? "0 8px" : "0 24px",
          boxSizing: "border-box",
          minWidth: 0,
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <PageHeader title="Sell" subtitle="Browse active buylist products and create an order." />
        </div>

        {renderBrandBar()}

        <main style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 10, minWidth: 0 }}>
            <h2 style={{ margin: 0, color: "#fff", fontSize: isPhone ? 21 : 24, fontWeight: 900, lineHeight: 1.18, overflowWrap: "anywhere" }}>
              {selectedBrand?.name || "Catalog"}
            </h2>
            {selectedBrand && (
              <span style={{ color: "#9fb3c8", fontSize: 14, fontWeight: 600 }}>
                {productTotal || 0} products
              </span>
            )}
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: hasSetRail ? 0 : 16,
              minWidth: 0,
            }}
          >
            <div style={{ position: "relative", flex: "1 1 420px", minWidth: isPhone ? "100%" : 280 }}>
              <Search size={15} style={{ position: "absolute", top: 12, left: 12, color: "#8b9296" }} />
              <Input
                value={productSearch}
                disabled={!selectedBrandId}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  setPage(0);
                }}
                placeholder="Search products..."
                style={{ paddingLeft: 36, height: 42 }}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: isPhone ? "stretch" : "flex-end",
                flex: isPhone ? "1 1 100%" : "0 0 auto",
                width: isPhone ? "100%" : undefined,
                minWidth: 0,
              }}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={!selectedBrandId}
                    style={{
                      height: 42,
                      minWidth: 0,
                      width: isPhone ? undefined : 158,
                      flex: isPhone ? "1 1 0" : "0 0 auto",
                      borderRadius: 10,
                    }}
                  >
                    {sortOption.label}
                    <ChevronDown size={14} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {SORT_OPTIONS.map((option) => (
                    <DropdownMenuItem
                      key={option.label}
                      active={option.label === sortOption.label}
                      onClick={() => {
                        setSortOption(option);
                        setPage(0);
                      }}
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <div style={{ flexShrink: 0 }}>
                <ViewToggle viewMode={viewMode} onChange={handleViewChange} height={42} buttonSize={34} />
              </div>
              <Button
                onClick={handleOpenOrderDialog}
                disabled={selectedProducts.length === 0}
                style={{
                  height: 42,
                  width: isPhone ? "100%" : undefined,
                  minWidth: isPhone ? 0 : 146,
                  flex: isPhone ? "1 1 100%" : "0 0 auto",
                  borderRadius: 10,
                  fontWeight: 800,
                  boxShadow: selectedProducts.length > 0 ? "0 10px 24px -18px rgba(0,173,181,0.85)" : "none",
                }}
              >
                Create Order{selectedProducts.length > 0 ? ` (${selectedProducts.length})` : ""}
              </Button>
            </div>
          </div>

          {selectedBrandId && renderSetChips()}

          {!selectedBrandId ? (
            <EmptyState title="No categories yet" description="Brands will appear here after the DB setup and PQC categorization import are complete." />
          ) : loadingProducts && products.length === 0 ? (
            <EmptyState title="Loading products..." />
          ) : products.length === 0 ? (
            <EmptyState title="No products found" description="Try a different brand, set, or search term." />
          ) : (
            <div style={{ position: "relative", minHeight: products.length ? 260 : undefined }}>
              <div
                style={{
                  opacity: loadingProducts ? 0.58 : 1,
                  pointerEvents: loadingProducts ? "none" : "auto",
                  transition: "opacity 0.16s ease",
                }}
              >
                {viewMode === "card" ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isPhone ? "1fr" : `repeat(auto-fill, minmax(${isMobile ? "210px" : "246px"}, 1fr))`,
                      gap: isPhone ? 12 : 16,
                    }}
                  >
                    {products.map(renderProductCard)}
                  </div>
                ) : (
                  renderProductList()
                )}
              </div>
            </div>
          )}

          {selectedBrandId && products.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: isPhone ? "space-between" : "flex-end",
                gap: 10,
                marginTop: 16,
                flexWrap: "wrap",
              }}
            >
              <span style={{ color: "#9fb3c8", fontSize: 12 }}>
                {page * ROWS_PER_PAGE + 1}-{page * ROWS_PER_PAGE + products.length}
                {productTotal ? ` of ${productTotal}` : ""}
              </span>
              <Button variant="outline" size="sm" disabled={!canGoPrev} onClick={() => setPage((prev) => Math.max(0, prev - 1))}>
                Prev
              </Button>
              <Button variant="outline" size="sm" disabled={!canGoNext} onClick={() => setPage((prev) => prev + 1)}>
                Next
              </Button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

const CountBadge = ({ active, children }) => (
  <span
    style={{
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 24,
      height: 20,
      padding: "0 7px",
      borderRadius: 999,
      border: active ? "1px solid rgba(159,223,255,0.36)" : "1px solid rgba(255,255,255,0.11)",
      background: active ? "rgba(159,223,255,0.16)" : "rgba(255,255,255,0.055)",
      color: active ? "#d9f8ff" : "#d0d8de",
      fontFamily: "ui-monospace, monospace",
      fontSize: 11,
      fontWeight: 800,
      lineHeight: 1,
    }}
  >
    {children}
  </span>
);

const MobileSelectorButton = ({ disabled, label, meta, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    style={{
      width: "100%",
      minHeight: 44,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      borderRadius: 10,
      border: "1px solid rgba(255,255,255,0.1)",
      background: disabled ? "rgba(255,255,255,0.025)" : "#1e1e1e",
      color: disabled ? "#6f777d" : "#fff",
      padding: "9px 12px",
      cursor: disabled ? "not-allowed" : "pointer",
      boxSizing: "border-box",
      textAlign: "left",
    }}
  >
    <span style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
      <Boxes size={14} style={{ flexShrink: 0, color: "#9fb3c8" }} />
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13.5, fontWeight: 800 }}>
        {label}
      </span>
    </span>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0, color: "#9fb3c8", fontSize: 12, fontWeight: 700 }}>
      {meta && <CountBadge>{meta}</CountBadge>}
      <ChevronDown size={14} />
    </span>
  </button>
);

const PickerRow = ({ active, label, meta, onClick }) => (
  <button
    type="button"
    disabled={!onClick}
    onClick={onClick}
    style={{
      width: "100%",
      minHeight: 42,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      borderRadius: 9,
      border: active ? "1px solid rgba(0,173,181,0.45)" : "1px solid rgba(255,255,255,0.08)",
      background: active ? "rgba(0,173,181,0.14)" : "#14191d",
      color: active ? "#fff" : "#c3c8cc",
      padding: "9px 11px",
      cursor: onClick ? "pointer" : "default",
      boxSizing: "border-box",
      textAlign: "left",
    }}
  >
    <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13, fontWeight: 800 }}>
      {label}
    </span>
    {meta && (
      <CountBadge active={active}>{meta}</CountBadge>
    )}
  </button>
);

const BrandChip = ({ active, name, count, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flexShrink: 0,
        minHeight: 38,
        maxWidth: 220,
        display: "inline-flex",
        alignItems: "center",
        gap: 9,
        borderRadius: 18,
        border: active
          ? "1px solid rgba(0,173,181,0.5)"
          : hovered
            ? "1px solid rgba(255,255,255,0.16)"
            : "1px solid rgba(255,255,255,0.08)",
        background: active ? "rgba(0,173,181,0.16)" : hovered ? "rgba(255,255,255,0.045)" : "#1e1e1e",
        color: active ? "#fff" : "#c3c8cc",
        padding: "8px 13px",
        fontSize: 13,
        fontWeight: active ? 800 : 700,
        cursor: "pointer",
        transition: "background 0.15s, border-color 0.15s, color 0.15s",
      }}
    >
      <Boxes size={13} style={{ flexShrink: 0 }} />
      <span style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {name}
      </span>
      <CountBadge active={active}>{count}</CountBadge>
    </button>
  );
};

const Chip = ({ active, children, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flexShrink: 0,
        maxWidth: 220,
        minHeight: 38,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        borderRadius: 18,
        border: active
          ? "1px solid rgba(0,173,181,0.42)"
          : hovered
            ? "1px solid rgba(255,255,255,0.15)"
            : "1px solid rgba(255,255,255,0.08)",
        background: active ? "rgba(0,173,181,0.13)" : hovered ? "rgba(255,255,255,0.045)" : "#1e1e1e",
        color: active ? "#fff" : "#c3c8cc",
        padding: "8px 14px",
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
        transition: "background 0.15s, border-color 0.15s, color 0.15s",
      }}
    >
      {children}
    </button>
  );
};

const StepperButton = ({ children, disabled, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    style={{
      width: 30,
      height: 30,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#1e1e1e",
      border: "1px solid #2a2a2e",
      color: disabled ? "#4a4a4e" : "#fff",
      cursor: disabled ? "not-allowed" : "pointer",
    }}
  >
    {children}
  </button>
);

const EmptyState = ({ title, description, compact }) => (
  <div
    style={{
      minHeight: compact ? 80 : 220,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      border: compact ? "none" : "1px dashed rgba(255,255,255,0.12)",
      borderRadius: 12,
      color: "#9fb3c8",
      textAlign: "center",
      padding: compact ? 12 : 24,
    }}
  >
    {!compact && <PackageSearch size={26} style={{ marginBottom: 10 }} />}
    <div style={{ color: "#fff", fontSize: compact ? 13 : 15, fontWeight: 800 }}>{title}</div>
    {description && <div style={{ maxWidth: 420, fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>{description}</div>}
  </div>
);

export default SellPage;
